import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import CreatorNavbar from '../components/dashboard/CreatorNavbar';
import CreatorPageHeader from '../components/dashboard/CreatorPageHeader';
import api, { collaboratorClubAPI } from '../api';

const CollaboratorClubPage = () => {
  const { user } = useAuth();
  const userId = user?.userId;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [eligibleProjects, setEligibleProjects] = useState([]);
  const [posts, setPosts] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [states, setStates] = useState([]);
  const [interests, setInterests] = useState([]);
  const [selectedInterest, setSelectedInterest] = useState('');

  const [requestsByPost, setRequestsByPost] = useState({});
  const [tasksByPost, setTasksByPost] = useState({});

  const [postForm, setPostForm] = useState({
    projectId: '',
    title: '',
    description: '',
    maxCollaborators: 1,
    visibilityInterests: '',
    visibilityStates: []
  });

  const [taskForms, setTaskForms] = useState({});
  const [subtaskForms, setSubtaskForms] = useState({});

  const canCreatePost = useMemo(
    () => postForm.projectId && postForm.title.trim() && postForm.description.trim() && postForm.visibilityStates.length > 0,
    [postForm]
  );
  const selectedVisibilityInterests = useMemo(
    () => postForm.visibilityInterests.split(',').map((item) => item.trim()).filter(Boolean),
    [postForm.visibilityInterests]
  );
  const availableInterestOptions = useMemo(
    () => interests.filter((interest) => !selectedVisibilityInterests.includes(interest.name)),
    [interests, selectedVisibilityInterests]
  );
  const normalizeStates = (payload) => (
    Array.isArray(payload)
      ? payload
          .map((state, index) => {
            if (typeof state === 'string') {
              return { id: `state-${index}`, stateName: state };
            }
            if (state && typeof state === 'object') {
              const stateName = state.stateName || state.statename || state.name || '';
              if (!stateName) {
                return null;
              }
              return {
                id: state.id || `state-${index}`,
                stateName
              };
            }
            return null;
          })
          .filter(Boolean)
      : []
  );

  const showToast = (message, isError = false) => {
    if (isError) {
      setError(message);
      setSuccess('');
    } else {
      setSuccess(message);
      setError('');
    }
  };

  const loadData = async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const [visiblePostsResult, ownerPostsResult, projectsResult, statesResult, interestsResult] = await Promise.allSettled([
        collaboratorClubAPI.getVisiblePosts(userId),
        collaboratorClubAPI.getMyPosts(userId),
        collaboratorClubAPI.getEligibleProjects(userId),
        api.get('/states'),
        api.get('/interests/grouped')
      ]);
      const errors = [];

      if (visiblePostsResult.status === 'fulfilled') {
        setPosts(Array.isArray(visiblePostsResult.value) ? visiblePostsResult.value : []);
      } else {
        setPosts([]);
        errors.push(visiblePostsResult.reason?.response?.data?.error || 'visible Creator Hub posts');
      }

      if (ownerPostsResult.status === 'fulfilled') {
        setMyPosts(Array.isArray(ownerPostsResult.value) ? ownerPostsResult.value : []);
      } else {
        setMyPosts([]);
        errors.push(ownerPostsResult.reason?.response?.data?.error || 'your Creator Hub posts');
      }

      if (projectsResult.status === 'fulfilled') {
        setEligibleProjects(Array.isArray(projectsResult.value) ? projectsResult.value : []);
      } else {
        setEligibleProjects([]);
        errors.push(projectsResult.reason?.response?.data?.error || 'eligible projects');
      }

      if (statesResult.status === 'fulfilled') {
        setStates(normalizeStates(statesResult.value?.data));
      } else {
        setStates([]);
        errors.push(statesResult.reason?.response?.data?.error || 'allowed states');
      }

      if (interestsResult.status === 'fulfilled') {
        const groupedInterests = interestsResult.value?.data && typeof interestsResult.value.data === 'object'
          ? Object.entries(interestsResult.value.data).flatMap(([category, items]) =>
              Array.isArray(items)
                ? items.map((item) => ({
                    id: `${category}-${item}`,
                    category,
                    name: item
                  }))
                : []
            )
          : [];
        setInterests(groupedInterests);
      } else {
        setInterests([]);
        errors.push(interestsResult.reason?.response?.data?.error || 'visibility interests');
      }

      if (errors.length > 0) {
        showToast(`Some Creator Hub data could not be loaded: ${errors[0]}.`, true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const toggleStateSelection = (stateName) => {
    setPostForm((prev) => ({
      ...prev,
      visibilityStates: prev.visibilityStates.includes(stateName)
        ? prev.visibilityStates.filter((s) => s !== stateName)
        : [...prev.visibilityStates, stateName]
    }));
  };

  const addVisibilityInterest = () => {
    if (!selectedInterest) return;
    setPostForm((prev) => ({
      ...prev,
      visibilityInterests: [...selectedVisibilityInterests, selectedInterest].join(',')
    }));
    setSelectedInterest('');
  };

  const removeVisibilityInterest = (interestName) => {
    setPostForm((prev) => ({
      ...prev,
      visibilityInterests: prev.visibilityInterests
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item && item !== interestName)
        .join(',')
    }));
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!userId) return;
    setSubmitting(true);
    setError('');
    try {
      await collaboratorClubAPI.createPost(userId, {
        ...postForm,
        projectId: Number(postForm.projectId),
        maxCollaborators: Number(postForm.maxCollaborators)
      });
      showToast('Creator Hub post created');
      setPostForm({
        projectId: '',
        title: '',
        description: '',
        maxCollaborators: 1,
        visibilityInterests: '',
        visibilityStates: []
      });
      setSelectedInterest('');
      await loadData();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create post', true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinRequest = async (postId) => {
    if (!userId) return;
    const message = window.prompt('Optional message to owner:') || '';
    try {
      await collaboratorClubAPI.requestToJoin(postId, userId, message);
      showToast('Join request submitted');
      await loadData();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to submit join request', true);
    }
  };

  const loadRequests = async (postId) => {
    if (!userId) return;
    try {
      const data = await collaboratorClubAPI.getJoinRequests(postId, userId);
      setRequestsByPost((prev) => ({ ...prev, [postId]: data || [] }));
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to load requests', true);
    }
  };

  const reviewRequest = async (postId, requestId, approve) => {
    if (!userId) return;
    try {
      if (approve) {
        await collaboratorClubAPI.approveRequest(requestId, userId);
      } else {
        await collaboratorClubAPI.rejectRequest(requestId, userId);
      }
      await Promise.all([loadRequests(postId), loadData()]);
      showToast(approve ? 'Request approved' : 'Request rejected');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to review request', true);
    }
  };

  const loadTasks = async (postId) => {
    if (!userId) return;
    try {
      const data = await collaboratorClubAPI.getTasks(postId, userId);
      setTasksByPost((prev) => ({ ...prev, [postId]: data || [] }));
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to load tasks', true);
    }
  };

  const createTask = async (postId) => {
    if (!userId) return;
    const form = taskForms[postId] || {};
    if (!form.title?.trim()) {
      showToast('Task title is required', true);
      return;
    }
    try {
      await collaboratorClubAPI.createTask(postId, userId, {
        title: form.title,
        description: form.description || '',
        dueDate: form.dueDate || null,
        assignedToUserId: form.assignedToUserId ? Number(form.assignedToUserId) : null
      });
      setTaskForms((prev) => ({ ...prev, [postId]: { title: '', description: '', dueDate: '', assignedToUserId: '' } }));
      await loadTasks(postId);
      showToast('Task created');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create task', true);
    }
  };

  const updateTaskStatus = async (postId, taskId, status) => {
    if (!userId) return;
    try {
      await collaboratorClubAPI.updateTaskStatus(taskId, userId, status);
      await loadTasks(postId);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update task status', true);
    }
  };

  const createSubtask = async (postId, taskId) => {
    if (!userId) return;
    const key = `${postId}-${taskId}`;
    const form = subtaskForms[key] || {};
    if (!form.title?.trim()) {
      showToast('Subtask title is required', true);
      return;
    }
    try {
      await collaboratorClubAPI.createSubtask(taskId, userId, {
        title: form.title,
        assignedToUserId: form.assignedToUserId ? Number(form.assignedToUserId) : null
      });
      setSubtaskForms((prev) => ({ ...prev, [key]: { title: '', assignedToUserId: '' } }));
      await loadTasks(postId);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create subtask', true);
    }
  };

  const updateSubtaskStatus = async (postId, subtaskId, status) => {
    if (!userId) return;
    try {
      await collaboratorClubAPI.updateSubtaskStatus(subtaskId, userId, status);
      await loadTasks(postId);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update subtask status', true);
    }
  };

  if (!user) return <p className="p-8 text-center">Please log in to continue.</p>;
  if (user.role !== 'creator') return <p className="p-8 text-center">Access denied.</p>;

  return (
    <>
      <Helmet>
        <title>Creator Hub - CollabOrb</title>
      </Helmet>
      <CreatorNavbar />
      <div className="pt-16 sm:pt-20">
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
          <CreatorPageHeader
            kicker="Collaboration workspace"
            title="Creator Hub"
            description="Create collaboration posts, review creator requests, and manage campaign tasks."
          />

          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>}
          {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">{success}</div>}

          {loading ? (
            <div className="text-center py-12">Loading Creator Hub...</div>
          ) : (
            <>
              <section className="bg-white border rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Create Creator Hub Post</h2>
                <form onSubmit={handleCreatePost} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Eligible Project</label>
                    <select
                      value={postForm.projectId}
                      onChange={(e) => setPostForm((prev) => ({ ...prev, projectId: e.target.value }))}
                      className="w-full border rounded p-2"
                      required
                    >
                      <option value="">Select project</option>
                      {eligibleProjects.map((project) => (
                        <option key={project.projectId} value={project.projectId}>
                          {project.title} {project.brandName ? `- ${project.brandName}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Post Title</label>
                    <input
                      value={postForm.title}
                      onChange={(e) => setPostForm((prev) => ({ ...prev, title: e.target.value }))}
                      className="w-full border rounded p-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      value={postForm.description}
                      onChange={(e) => setPostForm((prev) => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full border rounded p-2"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Max Collaborators</label>
                      <input
                        type="number"
                        min={1}
                        value={postForm.maxCollaborators}
                        onChange={(e) => setPostForm((prev) => ({ ...prev, maxCollaborators: e.target.value }))}
                        className="w-full border rounded p-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Visibility Interests</label>
                      <p className="text-xs text-gray-500 mb-2">
                        Select one or more interests to limit this post to matching creators.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <select
                          value={selectedInterest}
                          onChange={(e) => setSelectedInterest(e.target.value)}
                          className="w-full border rounded p-2"
                        >
                          <option value="">Select an interest</option>
                          {availableInterestOptions.map((interest) => (
                            <option key={interest.id} value={interest.name}>
                              {interest.name} ({interest.category})
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={addVisibilityInterest}
                          disabled={!selectedInterest}
                          className="px-4 py-2 rounded bg-collaborb-inner text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Add
                        </button>
                      </div>
                      {selectedVisibilityInterests.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {selectedVisibilityInterests.map((interestName) => (
                            <button
                              key={interestName}
                              type="button"
                              onClick={() => removeVisibilityInterest(interestName)}
                              className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700 hover:bg-slate-200"
                            >
                              <span>{interestName}</span>
                              <span className="text-slate-400">×</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {interests.length === 0 && (
                        <p className="mt-2 text-xs text-gray-500">No interests are available right now.</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Allowed States (required)</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto border rounded p-3">
                      {states.map((state) => (
                        <label key={state.id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={postForm.visibilityStates.includes(state.stateName)}
                            onChange={() => toggleStateSelection(state.stateName)}
                          />
                          <span>{state.stateName}</span>
                        </label>
                      ))}
                      {states.length === 0 && (
                        <p className="col-span-full text-sm text-gray-500">No states are available right now.</p>
                      )}
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting || !canCreatePost}
                    className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
                  >
                    {submitting ? 'Creating...' : 'Create Post'}
                  </button>
                </form>
              </section>

              <section className="bg-white border rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Discover Posts</h2>
                {posts.length === 0 ? (
                  <p className="text-gray-500">No matching Creator Hub posts available.</p>
                ) : (
                  <div className="space-y-4">
                    {posts.map((post) => (
                      <div key={post.postId} className="border rounded p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-lg font-semibold">{post.title}</h3>
                            <p className="text-sm text-gray-500">Project: {post.projectTitle}</p>
                            <p className="text-sm text-gray-600 mt-1">{post.description}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              Slots: {post.approvedCollaboratorsCount}/{post.maxCollaborators} | Owner: {post.ownerName}
                            </p>
                          </div>
                          {!post.isOwner && (
                            <button
                              onClick={() => handleJoinRequest(post.postId)}
                              disabled={post.hasPendingRequest || post.isApprovedCollaborator || post.status !== 'OPEN'}
                              className="bg-emerald-600 text-white px-3 py-2 rounded disabled:opacity-50"
                            >
                              {post.isApprovedCollaborator ? 'Joined' : post.hasPendingRequest ? 'Requested' : 'Request Join'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="bg-white border rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">My Posts (Owner Panel)</h2>
                {myPosts.length === 0 ? (
                  <p className="text-gray-500">You have not created any Creator Hub posts yet.</p>
                ) : (
                  <div className="space-y-6">
                    {myPosts.map((post) => (
                      <div key={post.postId} className="border rounded p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <h3 className="font-semibold">{post.title}</h3>
                            <p className="text-sm text-gray-500">Project: {post.projectTitle}</p>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => loadRequests(post.postId)} className="border px-3 py-1 rounded">
                              Refresh Requests
                            </button>
                            <button onClick={() => loadTasks(post.postId)} className="border px-3 py-1 rounded">
                              Refresh Tasks
                            </button>
                          </div>
                        </div>

                        <div className="mt-4">
                          <h4 className="font-medium mb-2">Join Requests</h4>
                          {(requestsByPost[post.postId] || []).length === 0 ? (
                            <p className="text-sm text-gray-500">No requests loaded.</p>
                          ) : (
                            <div className="space-y-2">
                              {requestsByPost[post.postId].map((request) => (
                                <div key={request.requestId} className="border rounded p-3 flex items-center justify-between gap-3">
                                  <div>
                                    <p className="font-medium">{request.requesterName}</p>
                                    <p className="text-xs text-gray-500">
                                      State: {request.requesterState || 'N/A'} | Status: {request.status}
                                    </p>
                                    {request.message && <p className="text-sm text-gray-700 mt-1">{request.message}</p>}
                                  </div>
                                  {request.status === 'PENDING' && (
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => reviewRequest(post.postId, request.requestId, true)}
                                        className="bg-green-600 text-white px-3 py-1 rounded"
                                      >
                                        Approve
                                      </button>
                                      <button
                                        onClick={() => reviewRequest(post.postId, request.requestId, false)}
                                        className="bg-red-600 text-white px-3 py-1 rounded"
                                      >
                                        Reject
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="mt-6 border-t pt-4">
                          <h4 className="font-medium mb-2">Task Board</h4>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
                            <input
                              placeholder="Task title"
                              value={taskForms[post.postId]?.title || ''}
                              onChange={(e) => setTaskForms((prev) => ({ ...prev, [post.postId]: { ...prev[post.postId], title: e.target.value } }))}
                              className="border rounded p-2"
                            />
                            <input
                              placeholder="Description"
                              value={taskForms[post.postId]?.description || ''}
                              onChange={(e) => setTaskForms((prev) => ({ ...prev, [post.postId]: { ...prev[post.postId], description: e.target.value } }))}
                              className="border rounded p-2"
                            />
                            <input
                              type="date"
                              value={taskForms[post.postId]?.dueDate || ''}
                              onChange={(e) => setTaskForms((prev) => ({ ...prev, [post.postId]: { ...prev[post.postId], dueDate: e.target.value } }))}
                              className="border rounded p-2"
                            />
                            <button onClick={() => createTask(post.postId)} className="bg-blue-600 text-white px-3 py-2 rounded">
                              Add Task
                            </button>
                          </div>

                          {(tasksByPost[post.postId] || []).length === 0 ? (
                            <p className="text-sm text-gray-500">No tasks loaded.</p>
                          ) : (
                            <div className="space-y-3">
                              {tasksByPost[post.postId].map((task) => {
                                const subtaskKey = `${post.postId}-${task.taskId}`;
                                return (
                                  <div key={task.taskId} className="border rounded p-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <div>
                                        <p className="font-medium">{task.title}</p>
                                        {task.description && <p className="text-sm text-gray-600">{task.description}</p>}
                                      </div>
                                      <select
                                        value={task.status}
                                        onChange={(e) => updateTaskStatus(post.postId, task.taskId, e.target.value)}
                                        className="border rounded p-1 text-sm"
                                      >
                                        <option value="TODO">TODO</option>
                                        <option value="IN_PROGRESS">IN_PROGRESS</option>
                                        <option value="DONE">DONE</option>
                                      </select>
                                    </div>

                                    <div className="mt-3 space-y-2">
                                      {(task.subtasks || []).map((subtask) => (
                                        <div key={subtask.subtaskId} className="flex items-center justify-between gap-2 bg-gray-50 p-2 rounded">
                                          <span className="text-sm">{subtask.title}</span>
                                          <select
                                            value={subtask.status}
                                            onChange={(e) => updateSubtaskStatus(post.postId, subtask.subtaskId, e.target.value)}
                                            className="border rounded p-1 text-xs"
                                          >
                                            <option value="TODO">TODO</option>
                                            <option value="IN_PROGRESS">IN_PROGRESS</option>
                                            <option value="DONE">DONE</option>
                                          </select>
                                        </div>
                                      ))}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
                                      <input
                                        placeholder="New subtask"
                                        value={subtaskForms[subtaskKey]?.title || ''}
                                        onChange={(e) => setSubtaskForms((prev) => ({ ...prev, [subtaskKey]: { ...prev[subtaskKey], title: e.target.value } }))}
                                        className="border rounded p-2 text-sm"
                                      />
                                      <input
                                        placeholder="Assign userId (optional)"
                                        value={subtaskForms[subtaskKey]?.assignedToUserId || ''}
                                        onChange={(e) => setSubtaskForms((prev) => ({ ...prev, [subtaskKey]: { ...prev[subtaskKey], assignedToUserId: e.target.value } }))}
                                        className="border rounded p-2 text-sm"
                                      />
                                      <button
                                        onClick={() => createSubtask(post.postId, task.taskId)}
                                        className="bg-indigo-600 text-white px-3 py-2 rounded text-sm"
                                      >
                                        Add Subtask
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CollaboratorClubPage;
